import React, { useEffect, useRef } from "react";
import {
  astryx,
  ContentSidebarLayout,
  SectionCard,
  QuestionCard,
  InsertZone,
  DraggableRail,
  Plus,
  useDragAndDropLogic,
  makeClientId,
  type IScriptTemplate,
  type IScriptTemplateQuestion,
  type IScriptTemplateSection,
} from "@shadowcue/ui";

interface ScriptBuilderProps {
  values: IScriptTemplate;
  onChange: (values: IScriptTemplate) => void;
  errors: Record<string, string>;
  onClearError: (key: string) => void;
  /**
   * Renders every field read-only: the rail lists sections/questions for
   * navigation only (no drag handles, no delete), and the editor's fields
   * are disabled with add/delete controls hidden. Used by the edit page
   * when viewing a revision that is not the latest and cannot be edited.
   * @default false
   */
  readonly?: boolean;
}

// ============================================================================
// Exported helpers used by form_helpers.ts for validation and initialization
// ============================================================================

/**
 * Factory for blank questions. Used by script_builder for new items and
 * by form_helpers for template initialization and validation.
 */
export const blankQuestion = (): IScriptTemplateQuestion => ({
  clientId: makeClientId(),
  body: "",
  prospectiveResponse: "",
});

/**
 * Factory for blank sections. Used by script_builder for new items and
 * by form_helpers for template initialization and validation.
 */
export const blankSection = (): IScriptTemplateSection => ({
  clientId: makeClientId(),
  name: "",
  scriptTemplateQuestions: [blankQuestion()],
});

// ============================================================================
// Local helpers for script_builder component
// ============================================================================
const sectionSortableId = (section: IScriptTemplateSection): string =>
  `section:${section.clientId}`;

const questionSortableId = (question: IScriptTemplateQuestion): string =>
  `question:${question.clientId}`;

/**
 * Two-panel script builder: a thin NavRail (drag-and-drop reorder +
 * click-to-scroll navigation) beside the main editor, where each section is
 * a heading followed by its numbered question cards. Reorder only happens
 * in the rail — the main editor has no drag handles. Delete shows an
 * Astryx toast with an Undo action.
 */
const ScriptBuilder = ({
  values,
  onChange,
  errors,
  onClearError,
  readonly = false,
}: ScriptBuilderProps) => {
  // Astryx's toast is imperative/portal-rendered: the endContent we hand it
  // at call time is not re-rendered on every ScriptBuilder render, so its
  // Undo handler needs a way to read the *current* values at click time
  // rather than whatever `values` were in scope when the toast was created.
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const toast = astryx.useToast();

  // Orchestrate all drag-and-drop logic: sensors, collision detection, handlers, state
  const dragLogic = useDragAndDropLogic({ values, onChange });

  // Edit handlers for form fields
  const setSectionName = (sectionIndex: number, name: string) => {
    onClearError(`section-${sectionIndex}-name`);
    const newSections = values.scriptTemplateSections.map((section, i) =>
      i === sectionIndex ? { ...section, name } : section,
    );
    onChange({ ...values, scriptTemplateSections: newSections });
  };

  const updateQuestion = (
    sectionIndex: number,
    questionIndex: number,
    field: keyof IScriptTemplateQuestion,
    value: string,
  ) => {
    if (field === "body") {
      onClearError(`question-${sectionIndex}-${questionIndex}-body`);
    }
    const newSections = values.scriptTemplateSections.map((section, i) => {
      if (i !== sectionIndex) return section;
      const newQuestions = section.scriptTemplateQuestions.map((question, j) =>
        j === questionIndex ? { ...question, [field]: value } : question,
      );
      return { ...section, scriptTemplateQuestions: newQuestions };
    });
    onChange({ ...values, scriptTemplateSections: newSections });
  };

  // Inserts a blank section at the very start (sectionIndex -1, i.e. before
  // index 0). Now only called from the empty-state guard below — every
  // InsertZone's "Section" choice instead calls insertSectionAt, which
  // splits the section at that exact gap rather than appending a blank one
  // after it.
  const addSectionAfter = (sectionIndex: number) => {
    const newSection = blankSection();
    const newSections = [...values.scriptTemplateSections];
    newSections.splice(sectionIndex + 1, 0, newSection);
    onChange({ ...values, scriptTemplateSections: newSections });
    dragLogic.setEnteringClientId(newSection.clientId);
  };

  // Splices a blank question into `sectionIndex` at `questionIndex`, so an
  // InsertZone between two questions (or above the first / below the last)
  // can insert exactly at that gap rather than only appending.
  const insertQuestionAt = (sectionIndex: number, questionIndex: number) => {
    const newQuestion = blankQuestion();
    const newSections = values.scriptTemplateSections.map((section, i) => {
      if (i !== sectionIndex) return section;
      const newQuestions = [...section.scriptTemplateQuestions];
      newQuestions.splice(questionIndex, 0, newQuestion);
      return { ...section, scriptTemplateQuestions: newQuestions };
    });
    onChange({ ...values, scriptTemplateSections: newSections });
    dragLogic.setEnteringClientId(newQuestion.clientId);
  };

  // Splits `sectionIndex` at `questionIndex`: the existing section keeps
  // questions [0, questionIndex), and a new section — spliced in right
  // after it — takes the tail questions [questionIndex, end). The moved
  // questions keep their existing clientIds (same object references, via
  // `.slice`), so their dnd-kit sortable keys stay stable and they don't
  // replay the insert entrance animation — only the new section itself
  // does. If the gap is right at a section's end (no tail), the new
  // section is seeded with one blank question instead of starting empty,
  // matching what a blank section normally looks like.
  const insertSectionAt = (sectionIndex: number, questionIndex: number) => {
    const section = values.scriptTemplateSections[sectionIndex];
    const headQuestions = section.scriptTemplateQuestions.slice(0, questionIndex);
    const tailQuestions = section.scriptTemplateQuestions.slice(questionIndex);
    const newSection: IScriptTemplateSection = {
      clientId: makeClientId(),
      name: "",
      scriptTemplateQuestions: tailQuestions.length > 0 ? tailQuestions : [blankQuestion()],
    };
    const newSections = values.scriptTemplateSections.map((s, i) =>
      i === sectionIndex ? { ...s, scriptTemplateQuestions: headQuestions } : s,
    );
    newSections.splice(sectionIndex + 1, 0, newSection);
    onChange({ ...values, scriptTemplateSections: newSections });
    dragLogic.setEnteringClientId(newSection.clientId);
  };

  const deleteSection = (sectionIndex: number) => {
    const removed = values.scriptTemplateSections[sectionIndex];
    const newSections = values.scriptTemplateSections.filter((_, i) => i !== sectionIndex);
    onChange({ ...values, scriptTemplateSections: newSections });

    toast({
      body: "Section deleted",
      endContent: (
        <astryx.Button
          label="Undo"
          variant="ghost"
          size="sm"
          onClick={() => {
            const current = valuesRef.current;
            const restoredSections = [...current.scriptTemplateSections];
            restoredSections.splice(sectionIndex, 0, removed);
            onChange({ ...current, scriptTemplateSections: restoredSections });
          }}
        />
      ),
    });
  };

  const deleteQuestion = (sectionIndex: number, questionIndex: number) => {
    const removed =
      values.scriptTemplateSections[sectionIndex].scriptTemplateQuestions[questionIndex];
    const newSections = values.scriptTemplateSections.map((section, i) => {
      if (i !== sectionIndex) return section;
      return {
        ...section,
        scriptTemplateQuestions: section.scriptTemplateQuestions.filter(
          (_, j) => j !== questionIndex,
        ),
      };
    });
    onChange({ ...values, scriptTemplateSections: newSections });

    toast({
      body: "Question deleted",
      endContent: (
        <astryx.Button
          label="Undo"
          variant="ghost"
          size="sm"
          onClick={() => {
            const current = valuesRef.current;
            const restoredSections = current.scriptTemplateSections.map((section, i) => {
              if (i !== sectionIndex) return section;
              const restoredQuestions = [...section.scriptTemplateQuestions];
              restoredQuestions.splice(questionIndex, 0, removed);
              return { ...section, scriptTemplateQuestions: restoredQuestions };
            });
            onChange({ ...current, scriptTemplateSections: restoredSections });
          }}
        />
      ),
    });
  };

  // Render the navigation rail with drag-and-drop support using the reusable component
  const railContent = (
    <DraggableRail
      values={values}
      dragLogic={dragLogic}
      sectionSortableId={sectionSortableId}
      questionSortableId={questionSortableId}
      readonly={readonly}
      onDeleteSection={deleteSection}
      onDeleteQuestion={deleteQuestion}
      onInsertQuestion={insertQuestionAt}
      onInsertSection={insertSectionAt}
    />
  );

  // Read-only view has no InsertZones to carry spacing, so it keeps the
  // original gaps. The editable view zeroes them: InsertZone is now a fixed
  // 24px, in-flow element (not the zero-height overlay it used to be), so
  // it carries the spacing itself — a nonzero VStack gap here would stack
  // on top of that fixed height and double-space every card. Explicitly
  // typed as the literal union (rather than widened `number`) so it still
  // satisfies astryx.VStack's `gap` prop.
  const sectionGap: 0 | 8 = readonly ? 8 : 0;
  const questionGap: 0 | 4 = readonly ? 4 : 0;

  const mainContent = (
    <astryx.VStack gap={sectionGap}>
      {values.scriptTemplateSections.length === 0 && !readonly && (
        <astryx.Button
          label="Add section"
          icon={<Plus size={16} />}
          variant="ghost"
          onClick={() => addSectionAfter(-1)}
        />
      )}
      {values.scriptTemplateSections.map((section, i) => (
        <astryx.VStack
          gap={questionGap}
          key={section.clientId}
          ref={dragLogic.registerSectionEl(section.clientId)}
        >
          <SectionCard
            value={section.name}
            onChange={(name) => setSectionName(i, name)}
            placeholder="Untitled section"
            isDisabled={readonly}
            isEntering={section.clientId === dragLogic.enteringClientId}
            data-field-key={`section-${i}-name`}
            status={
              errors[`section-${i}-name`]
                ? { type: "error", message: errors[`section-${i}-name`] }
                : undefined
            }
            onDelete={readonly ? undefined : () => deleteSection(i)}
            deleteLabel="Delete section"
          />
          {/* One zone per gap — every gap offers both choices: Question
              inserts into this section at this exact spot; Section splits
              the section at this exact gap, moving everything from here on
              into a new section right after it. */}
          {!readonly && (
            <InsertZone
              isDisabled={dragLogic.isDragging}
              onInsertQuestion={() => insertQuestionAt(i, 0)}
              onInsertSection={() => insertSectionAt(i, 0)}
            />
          )}
          {section.scriptTemplateQuestions.map((question, j) => (
            <React.Fragment key={question.clientId}>
              <QuestionCard
                ref={dragLogic.registerQuestionEl(question.clientId)}
                index={j + 1}
                question={question.body}
                onQuestionChange={(value) => updateQuestion(i, j, "body", value)}
                questionStatus={
                  errors[`question-${i}-${j}-body`]
                    ? { type: "error", message: errors[`question-${i}-${j}-body`] }
                    : undefined
                }
                questionFieldKey={`question-${i}-${j}-body`}
                idealResponse={question.prospectiveResponse}
                onIdealResponseChange={(value) =>
                  updateQuestion(i, j, "prospectiveResponse", value)
                }
                isDisabled={readonly}
                isEntering={question.clientId === dragLogic.enteringClientId}
                onDelete={readonly ? undefined : () => deleteQuestion(i, j)}
                deleteLabel="Delete question"
              />
              {/* For the very last question of the very last section, this
                  same zone doubles as the trailing gap after the last
                  card; for any other question it's an ordinary between- or
                  boundary-gap zone — same code path either way. */}
              {!readonly && (
                <InsertZone
                  isDisabled={dragLogic.isDragging}
                  onInsertQuestion={() => insertQuestionAt(i, j + 1)}
                  onInsertSection={() => insertSectionAt(i, j + 1)}
                />
              )}
            </React.Fragment>
          ))}
        </astryx.VStack>
      ))}
    </astryx.VStack>
  );

  return <ContentSidebarLayout sidebar={railContent}>{mainContent}</ContentSidebarLayout>;
};

export default ScriptBuilder;
